import { addAssetToContainer } from '../../libs/buildChildrenForScene';
import type { Interpolation } from '../../types';
import type { BaseAsset } from '../asset/BaseAsset';
import { BaseGroup } from '../BaseGroup';
import type { DynamicStylingMixinInterface } from '../dynamicStylingMixin';

type MorphableAsset = BaseAsset & DynamicStylingMixinInterface;

type MorphSourceState = {
    asset: MorphableAsset;
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    alpha: number;
};

export class MorphManager extends BaseGroup {
    override _classType = 'MorphManager';

    #sourceStates: Map<string, MorphSourceState> = new Map();

    override get propertiesToSerialize() {
        return [];
    }

    setupMorphAnimations() {
        const nextScene = this.slide.nextScene;
        if (!nextScene) {
            return;
        }

        const nextSceneAssets = nextScene.getAssets({ deep: true }) as BaseAsset[];

        this.slide.getAssets({ deep: true }).forEach((thisAsset) => {
            const matchingAsset = nextSceneAssets.find((nextAsset) =>
                thisAsset.isMorphMatch(nextAsset),
            );
            if (!matchingAsset) {
                return;
            }

            this.#setupMorphForAssetPair(thisAsset as MorphableAsset, matchingAsset);
        });

        for (const { asset } of this.#sourceStates.values()) {
            if (!asset.hasMorphAnimation) {
                continue;
            }
            MorphManager.#forceBuildAndDraw(asset);
        }

        this._childAssets.forEach((clone) => {
            const morphableClone = clone as MorphableAsset;
            if (!morphableClone.hasMorphAnimation) {
                return;
            }
            MorphManager.#forceBuildAndDraw(morphableClone);
        });
    }

    clearMorphAnimations() {
        for (const state of this.#sourceStates.values()) {
            const { asset, x, y, scaleX, scaleY, alpha } = state;

            asset?.clearMorphAnimations();

            asset.transformation.position.x = x;
            asset.transformation.position.y = y;
            asset.scale.x = scaleX;
            asset.scale.y = scaleY;
            asset.alpha = alpha;

            MorphManager.#forceBuildAndDraw(asset);
        }
        this.#sourceStates.clear();

        [...this._childAssets].forEach((clone) => {
            this.removeChild(clone);
            clone.destroy();
        });
    }

    #setupMorphForAssetPair(sourceAsset: MorphableAsset, targetAsset: BaseAsset) {
        const { startPoint, interpolation } = sourceAsset.morphParameters;
        const maxDuration = this.slide.duration / 2;
        const availableDuration = this.slide.duration * (1 - startPoint);
        const morphDuration = Math.min(availableDuration, maxDuration);

        if (!this.#sourceStates.has(sourceAsset.id)) {
            this.#sourceStates.set(sourceAsset.id, {
                asset: sourceAsset,
                x: sourceAsset.transformation.position.x,
                y: sourceAsset.transformation.position.y,
                scaleX: sourceAsset.scale.x,
                scaleY: sourceAsset.scale.y,
                alpha: sourceAsset.alpha,
            });
        }

        const targetClone = this.#cloneAssetForMorph(targetAsset, sourceAsset);

        MorphManager.#setupCoreAnimations(
            sourceAsset,
            targetAsset,
            targetClone,
            morphDuration,
            interpolation,
        );
    }

    #cloneAssetForMorph(targetAsset: BaseAsset, sourceAsset: BaseAsset): MorphableAsset {
        const savedData = targetAsset.save();

        const clone = addAssetToContainer(this, savedData.classType) as MorphableAsset;
        clone.load(savedData);

        clone.transformation.position.x = sourceAsset.transformation.position.x;
        clone.transformation.position.y = sourceAsset.transformation.position.y;
        clone.transformation.anchor.x = sourceAsset.transformation.anchor.x;
        clone.transformation.anchor.y = sourceAsset.transformation.anchor.y;

        clone.alpha = 0;
        clone.autoDraw = sourceAsset.autoDraw;
        clone.morphParameters = { ...sourceAsset.morphParameters };

        return clone;
    }

    static #setupCoreAnimations(
        sourceAsset: MorphableAsset,
        targetAsset: BaseAsset,
        targetClone: MorphableAsset,
        morphDuration: number,
        interpolation: Interpolation,
    ) {
        // TODO: LU-3903 -- figure out why this is necessary - it is somehow related to the scale calculations
        MorphManager.#forceBuildAndDraw(sourceAsset);
        MorphManager.#forceBuildAndDraw(targetAsset);

        const sourcePos = sourceAsset.transformation.position;
        const targetPos = targetAsset.transformation.position;

        const deltaX = targetPos.x - sourcePos.x;
        const deltaY = targetPos.y - sourcePos.y;

        if (deltaX !== 0) {
            const sourceXAnim = sourceAsset.addMorphAnimation();
            sourceXAnim.property = 'transformation.position.x';
            sourceXAnim.startValue = 0;
            sourceXAnim.endValue = deltaX;
            sourceXAnim.shouldOffsetPropertyValue = true;
            sourceXAnim.duration = morphDuration;
            sourceXAnim.interpolation = interpolation;

            const cloneXAnim = targetClone.addMorphAnimation();
            cloneXAnim.property = 'transformation.position.x';
            cloneXAnim.startValue = 0;
            cloneXAnim.endValue = deltaX;
            cloneXAnim.shouldOffsetPropertyValue = true;
            cloneXAnim.duration = morphDuration;
            cloneXAnim.interpolation = interpolation;
        }

        if (deltaY !== 0) {
            const sourceYAnim = sourceAsset.addMorphAnimation();
            sourceYAnim.property = 'transformation.position.y';
            sourceYAnim.startValue = 0;
            sourceYAnim.endValue = deltaY;
            sourceYAnim.shouldOffsetPropertyValue = true;
            sourceYAnim.duration = morphDuration;
            sourceYAnim.interpolation = interpolation;

            const cloneYAnim = targetClone.addMorphAnimation();
            cloneYAnim.property = 'transformation.position.y';
            cloneYAnim.startValue = 0;
            cloneYAnim.endValue = deltaY;
            cloneYAnim.shouldOffsetPropertyValue = true;
            cloneYAnim.duration = morphDuration;
            cloneYAnim.interpolation = interpolation;
        }

        const scaleRatioX = sourceAsset.width > 0 ? targetAsset.width / sourceAsset.width : 1;
        const scaleRatioY = sourceAsset.height > 0 ? targetAsset.height / sourceAsset.height : 1;

        if (scaleRatioX !== 1) {
            const sourceScaleXAnim = sourceAsset.addMorphAnimation();
            sourceScaleXAnim.property = 'scale.x';
            sourceScaleXAnim.startValue = 1;
            sourceScaleXAnim.endValue = scaleRatioX;
            sourceScaleXAnim.duration = morphDuration;
            sourceScaleXAnim.interpolation = interpolation;

            const inverseScaleRatioX = scaleRatioX > 0 ? 1 / scaleRatioX : 1;
            const cloneScaleXAnim = targetClone.addMorphAnimation();
            cloneScaleXAnim.property = 'scale.x';
            cloneScaleXAnim.startValue = inverseScaleRatioX;
            cloneScaleXAnim.endValue = 1;
            cloneScaleXAnim.duration = morphDuration;
            cloneScaleXAnim.interpolation = interpolation;
        }

        if (scaleRatioY !== 1) {
            const sourceScaleYAnim = sourceAsset.addMorphAnimation();
            sourceScaleYAnim.property = 'scale.y';
            sourceScaleYAnim.startValue = 1;
            sourceScaleYAnim.endValue = scaleRatioY;
            sourceScaleYAnim.duration = morphDuration;
            sourceScaleYAnim.interpolation = interpolation;

            const inverseScaleRatioY = scaleRatioY > 0 ? 1 / scaleRatioY : 1;
            const cloneScaleYAnim = targetClone.addMorphAnimation();
            cloneScaleYAnim.property = 'scale.y';
            cloneScaleYAnim.startValue = inverseScaleRatioY;
            cloneScaleYAnim.endValue = 1;
            cloneScaleYAnim.duration = morphDuration;
            cloneScaleYAnim.interpolation = interpolation;
        }

        const sourceAlphaAnim = sourceAsset.addMorphAnimation();
        sourceAlphaAnim.property = 'alpha';
        sourceAlphaAnim.startValue = sourceAsset.alpha;
        sourceAlphaAnim.endValue = 0;
        sourceAlphaAnim.duration = morphDuration;
        sourceAlphaAnim.interpolation = interpolation;

        const cloneAlphaAnim = targetClone.addMorphAnimation();
        cloneAlphaAnim.property = 'alpha';
        cloneAlphaAnim.startValue = 0;
        cloneAlphaAnim.endValue = targetAsset.alpha;
        cloneAlphaAnim.duration = morphDuration;
        cloneAlphaAnim.interpolation = interpolation;
    }

    static #forceBuildAndDraw(asset: BaseAsset) {
        const prevAutoDraw = asset.autoDraw;
        asset.autoDraw = true;
        asset.requiresRedraw = true;
        asset._buildAndDrawRecur();
        asset.autoDraw = prevAutoDraw;
    }
}
